import { getCurrentTime, isArray, isString } from '@qctsw/utils'
import {
  getComplaintCountStatisticsApi,
  getIssueStatisticsApi,
  getReputationDetailApi,
  getReputationNewComplaintApi,
  modelListApi,
} from '~/server'
import type {
  ComplaintCountStatisticsParams,
  ComplaintCountStatisticsResponse,
  IssueStatisticsParams,
  IssueStatisticsResponse,
  ModelResponse,
  ReputationDetailResponse,
  ReputationNewComplaintItem,
} from '~/server/types'
import { useGetRouteParam } from '~/shared/function'
import { LOAD_STATE } from '~/shared/types'
import type { ISSUE_TYPE_CODE } from '~/shared/constants'

export enum ModelYear {
  all = '所有款',
}

export interface SeriesDetailLabel {
  quality: Labels[]
  sale: Labels[]
  afterSale: Labels[]
}
export interface Labels {
  content: string
  tips: string
}
export interface ComplaintIssueStatistics {
  source: IssueStatisticsResponse[]
  keys: string[]
  counts: number[]
  labels: Labels[]
}

export function logicReputationDetail() {
  // const { changeMeta } = useSite()
  const state = ref(LOAD_STATE.INIT)
  const detailData = ref<ReputationDetailResponse | null>(null)

  // function setMeta() {
  //   const data = detailData.value
  //   if (!data)
  //     return
  //   const title = `${data.carSeriesName} - ${data.carBrandName} - 口碑 - 汽车投诉网`
  //   const description = `${data.carVendorName}${data.carSeriesName}(${data.seriesBodys
  //     } -${data.seriesDisplacements})，优点：${data.seriesMerits}，缺点：${data.seriesDefects} `
  //   changeMeta({
  //     title,
  //     description,
  //     keywords: [data.carBrandName, data.carVendorName, data.carSeriesName, data.seriesBodys, data.seriesDisplacements],
  //   })
  // }
  async function getDetail(id: string) {
    const res = await useLoadState({
      key: `口碑详情-${id}`,
      state,
      fn: () => getReputationDetailApi(id),
    })

    if (!res?.data)
      return

    detailData.value = res.data
    // setMeta()
  }
  return {
    state,
    detailData,
    getDetail,
  }
}

export function logicReputationStatistics() {
  const seriesId = useGetRouteParam('seriesId', '')

  const modelId = ref<number | undefined>(undefined)
  const modelYear = ref<number | undefined>(undefined)
  watch(modelYear, (nv, ov) => {
    if (nv !== ov)
      modelId.value = undefined
  })

  const complaintCountStatistics = ref<{
    source: ComplaintCountStatisticsResponse[]
    year: number[]
    data: number[]
  }>({
    source: [],
    year: [],
    data: [],
  })
  const complaintIssueStatistics = ref<ComplaintIssueStatistics>({
    source: [],
    keys: [],
    counts: [],
    labels: [],
  })

  function initHandle(year?: string) {
    if (year && isString(year)) {
      if (!Number.isNaN(Number.parseInt(year)))
        modelYear.value = Number.parseInt(year)
      else if (year === ModelYear.all || Number.isNaN(Number.parseInt(year)))
        modelYear.value = undefined
    }
  }

  // 格式化投诉数
  function formatCount(data: ComplaintCountStatisticsResponse[]) {
    if (!isArray(data))
      throw new Error('formatCount data is not array')
    let year: number[] = []
    let complaintCountData: number[] = []
    for (const item of data) {
      year.push(item.complainYear)
      complaintCountData.push(item.complainTotal)
    }
    if (!data.length) {
      const currentYear = Number.parseInt(getCurrentTime('YYYY'))
      year = [currentYear, currentYear - 1, currentYear - 2]
      complaintCountData = [0, 0, 0]
    }

    return {
      year,
      complaintCountData,
    }
  }
  // 格式化问题
  function formatIssue(data: IssueStatisticsResponse[], issueTypeCode: keyof typeof ISSUE_TYPE_CODE = 1) {
    const keys: string[] = []
    const counts: number[] = []

    for (const item of data) {
      // 质量问题
      if (item.issueTypeCode === issueTypeCode) {
        keys.push(item.issueLabel)
        counts.push(item.complainTotal)
      }
    }
    return {
      keys,
      counts,
    }
  }
  // 格式化问题，以投诉量排序
  function formatIssueComplaintSort(data: IssueStatisticsResponse[]) {
    const labels: Labels[] = []
    // 过滤诉求
    data = data.filter(v => v.issueTypeCode !== 4)
    // 排序
    data = data.sort((a, b) => b.complainTotal - a.complainTotal)
    for (const item of data) {
      labels.push({
        content: item.issueLabel,
        tips: `${item.complainTotal}个`,
      })
    }
    return { labels }
  }

  const issueStatisticsState = ref(LOAD_STATE.INIT)
  // 按年投诉统计
  async function getIssueStat(params?: Partial<IssueStatisticsParams>, _id?: string) {
    const data: IssueStatisticsParams = Object.assign({
      seriesId: seriesId.value,
      modelId: modelId.value,
      modelYear: modelYear.value,
    }, params)

    const res = await useLoadState({
      key: `投诉问题统计-${modelId.value}-${modelYear.value}`,
      state: issueStatisticsState,
      fn: () => getIssueStatisticsApi(data, _id),
    })
    if (!res)
      return
    const { keys, counts } = formatIssue(res.data)
    const { labels } = formatIssueComplaintSort(res.data)
    complaintIssueStatistics.value = {
      source: res.data,
      keys,
      counts,
      labels,
    }
  }
  // 按年投诉统计
  const complaintCountStatisticsState = ref(LOAD_STATE.INIT)
  async function getComplaintCountStat(params?: ComplaintCountStatisticsParams) {
    params = Object.assign({
      seriesId: seriesId.value,
      modelId: modelId.value,
      modelYear: modelYear.value,
    }, params)

    const res = await useLoadState({
      key: `投诉统计-${modelId.value}-${modelYear.value}`,
      state: complaintCountStatisticsState,
      fn: () => getComplaintCountStatisticsApi(params),
    })

    if (!res)
      return
    const { year, complaintCountData } = formatCount(res.data)
    complaintCountStatistics.value = {
      source: res.data,
      year,
      data: complaintCountData,
    }
  }
  return {
    modelId,
    complaintCountStatistics,
    complaintIssueStatistics,
    initHandle,
    getIssueStat,
    getComplaintCountStat,
  }
}

export function logicReputationModel() {
  const models = ref<Record<string, ModelResponse[]>>({})
  const currentYear = ref(ModelYear.all)
  const currentYearModels = computed(() => {
    if (Object.prototype.hasOwnProperty.call(models.value, currentYear.value)) {
      return models.value[currentYear.value]
    }
    else if (currentYear.value === ModelYear.all) {
      const list = []
      for (const key in models.value)
        list.unshift(...models.value[key])
      return list
    }
    else { return [] }
  })
  async function getModelList(seriesId: number | string) {
    if (!seriesId || Number.isNaN(Number(seriesId)))
      return
    await modelListApi({ seriesId: Number(seriesId) }).then((res) => {
      if (!res)
        return
      models.value = res.data
    })
  }

  // 通过model获取对应的model信息
  function getModelName(id: number) {
    if (!currentYearModels.value || !currentYearModels.value.length)
      return
    const data = currentYearModels.value.find(v => v.id === id)
    return data
  }

  return {
    currentYear,
    currentYearModels,
    models,
    getModelName,
    getModelList,
  }
}

export function logicSeriesNewComplaint() {
  const state = ref(LOAD_STATE.INIT)
  const newComplaintList = ref<ReputationNewComplaintItem[]>([])
  async function getSeriesNewComplaint(id: number) {
    const res = await useLoadState({
      key: `最新投诉-${id}`,
      state,
      fn: () => getReputationNewComplaintApi(id),
    })

    if (!res)
      return
    newComplaintList.value = res.data
  }
  return {
    state,
    newComplaintList,
    getSeriesNewComplaint,
  }
}
